import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { Activity, ActivityTiming, Dependency, KspWorkspace } from '@entities/ksp/types';
import { decimal, floatDays, linkLabel, shortDate } from '@entities/ksp/format';
import { Button } from '@shared/ui/Button';
import {
  DAY,
  at,
  buildTree,
  centered,
  defaultOpen,
  dragShift,
  linkPaths,
  pan,
  pathOf,
  place,
  spanDays,
  successorsOf,
  ticks,
  visibleRows,
  windowFor,
  zoneOffset,
  type Filter,
  type Row,
  type Span,
  type Work,
  type Zoom,
} from './gantt/model';
import { Chip, Small } from './parts';

/** Высота строки и шапки, px. Полоса работы — 7…19 px строки, базовый план — 21…25. */
const ROW = 30;
const HEAD = 46;
const MID = 13;
const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MONTHS_FULL = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];
const ZOOMS: [Zoom, string][] = [
  ['day', 'День'],
  ['week', 'Неделя'],
  ['month', 'Месяц'],
  ['all', 'Весь проект'],
];
const FLOAT_HELP =
  'Общий резерв — на сколько работу можно сдвинуть, не сорвав договорной или директивный срок. Отрицательный — работа уже выходит за срок.';

/* ── Панель ── */

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;
const Segmented = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 8px;
  overflow: hidden;
`;
const Segment = styled.button<{ $active?: boolean }>`
  padding: 6px 11px;
  font: inherit;
  font-size: 12.5px;
  white-space: nowrap;
  border: 0;
  border-right: 1px solid ${({ theme }) => theme.colors.borderStrong};
  cursor: pointer;
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.text.secondary)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  &:last-child {
    border-right: 0;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`;
const Search = styled.input`
  flex: 1;
  min-width: 220px;
  font: inherit;
  font-size: 13px;
  color: inherit;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 7px 10px;
`;
const Notice = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 13px;
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  background: ${({ theme }) => theme.colors.surface};
`;

/* ── Диаграмма ── */

const Board = styled.div<{ $side: boolean }>`
  display: grid;
  grid-template-columns: ${({ $side }) => ($side ? 'minmax(0, 1fr) 320px' : 'minmax(0, 1fr)')};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  overflow: hidden;
`;
const Scroller = styled.div<{ $short: boolean }>`
  height: ${({ $short }) => ($short ? 'min(52vh, 540px)' : 'min(72vh, 760px)')};
  min-height: ${({ $short }) => ($short ? 320 : 420)}px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
`;
/**
 * Ширина таблицы слева зависит от места (см. `layoutFor`): шкала справа не должна
 * сжиматься в полоску ни на ноутбуке, ни при открытой карточке.
 */
const columns = css<{ $left: number }>`
  display: grid;
  grid-template-columns: ${({ $left }) => $left}px minmax(0, 1fr);
`;
type Layout = 'full' | 'compact' | 'narrow';
/** Колонки таблицы: название, объём, дни, начало, окончание, резерв (узко — без части). */
const TEMPLATE: Record<Layout, string> = {
  full: 'minmax(0, 1fr) 78px 34px 64px 64px 64px',
  compact: 'minmax(0, 1fr) 64px 64px 64px',
  narrow: 'minmax(0, 1fr) 64px',
};
const cells = css<{ $layout: Layout }>`
  display: grid;
  grid-template-columns: ${({ $layout }) => TEMPLATE[$layout]};
  column-gap: 8px;
  align-items: center;
  ${({ $layout }) =>
    $layout === 'compact'
      ? css`
          & > :nth-child(2),
          & > :nth-child(3) {
            display: none;
          }
        `
      : $layout === 'narrow'
        ? css`
            & > :nth-child(2),
            & > :nth-child(3),
            & > :nth-child(4),
            & > :nth-child(5) {
              display: none;
            }
          `
        : ''}
`;
/** Ширина таблицы и набор колонок по ширине диаграммы (без карточки). */
function layoutFor(available: number): { left: number; layout: Layout } {
  if (available < 640)
    return { left: Math.max(200, Math.round(available * 0.55)), layout: 'narrow' };
  const left = Math.min(640, Math.max(380, Math.round(available * 0.5)));
  return { left, layout: left < 560 ? 'compact' : 'full' };
}
const HeadAction = styled.button`
  margin-left: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;
const Head = styled.div<{ $left: number }>`
  ${columns};
  position: sticky;
  top: 0;
  z-index: 4;
  height: ${HEAD}px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
`;
const HeadCells = styled.div<{ $layout: Layout }>`
  ${cells};
  padding: 0 8px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.muted};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  & > span {
    white-space: nowrap;
    overflow: hidden;
  }
  & > span:not(:first-child) {
    text-align: right;
  }
`;
const Scale = styled.div`
  position: relative;
  overflow: hidden;
`;
const TickLabel = styled.span.attrs<{ $left: number }>(({ $left }) => ({
  style: { left: `${$left}%` },
}))<{ $left: number; $major?: boolean }>`
  position: absolute;
  top: ${({ $major }) => ($major ? 4 : 25)}px;
  padding-left: 4px;
  white-space: nowrap;
  font-size: 11px;
  line-height: 15px;
  color: ${({ theme, $major }) => ($major ? theme.colors.text.secondary : theme.colors.text.muted)};
  font-weight: ${({ $major }) => ($major ? 600 : 400)};
  border-left: 1px solid
    ${({ theme, $major }) => ($major ? theme.colors.borderStrong : theme.colors.border)};
`;
const Body = styled.div.attrs<{ $height: number }>(({ $height }) => ({
  style: { height: `${$height}px` },
}))<{ $height: number; $left: number }>`
  ${columns};
  position: relative;
`;
const LeftCol = styled.div`
  position: relative;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
`;
const RightCol = styled.div`
  position: relative;
  overflow: hidden;
`;
const GridLine = styled.div.attrs<{ $left: number }>(({ $left }) => ({
  style: { left: `${$left}%` },
}))<{ $left: number; $major?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  border-left: 1px solid
    ${({ theme, $major }) => ($major ? theme.colors.borderStrong : theme.colors.border)};
  opacity: ${({ $major }) => ($major ? 1 : 0.6)};
  pointer-events: none;
`;
type Mark = 'selected' | 'before' | 'after' | undefined;
const markBackground = (mark: Mark, theme: { colors: { primarySoft: string; surface: string } }) =>
  mark === 'selected'
    ? theme.colors.primarySoft
    : mark
      ? `color-mix(in srgb, ${theme.colors.primarySoft} 50%, transparent)`
      : 'transparent';
const Line = styled.div.attrs<{ $top: number }>(({ $top }) => ({
  style: { top: `${$top}px` },
}))<{ $top: number; $mark?: Mark }>`
  position: absolute;
  left: 0;
  right: 0;
  height: ${ROW}px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme, $mark }) => markBackground($mark, theme)};
  cursor: pointer;
`;
const LeftLine = styled(Line)<{ $group?: boolean; $layout: Layout }>`
  ${cells};
  padding: 0 8px;
  font-size: 12.5px;
  ${({ theme, $group, $mark }) =>
    $group &&
    !$mark &&
    css`
      background: ${theme.colors.surface};
    `}
  ${({ $mark, theme }) =>
    $mark === 'selected' &&
    css`
      box-shadow: inset 3px 0 0 ${theme.colors.primary};
    `}
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
  & > span:not(:first-child) {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;
const Name = styled.span.attrs<{ $indent: number }>(({ $indent }) => ({
  style: { paddingLeft: `${$indent}px` },
}))<{ $indent: number; $group?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-weight: ${({ $group }) => ($group ? 600 : 400)};
  b {
    font-weight: inherit;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
const Count = styled.small`
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.text.muted};
`;
const Code = styled.code`
  flex-shrink: 0;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
`;
const Toggle = styled.span`
  flex-shrink: 0;
  width: 14px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
`;
const Tag = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  padding: 0 4px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.status.danger};
  border: 1px solid ${({ theme }) => theme.colors.status.danger};
`;
const Late = styled.span<{ $late: boolean }>`
  color: ${({ theme, $late }) => ($late ? theme.colors.status.danger : 'inherit')};
  font-weight: ${({ $late }) => ($late ? 600 : 400)};
`;
/** Заливка полосы: критическая — со штриховкой (видна и без различения красного). */
const fill = (color: string, critical: boolean, surface: string) =>
  critical
    ? `repeating-linear-gradient(135deg, ${color} 0 4px, color-mix(in srgb, ${color} 45%, ${surface}) 4px 7px)`
    : color;
const Bar = styled.div.attrs<{ $left: number; $width: number }>(({ $left, $width }) => ({
  style: { left: `${$left}%`, width: `${$width}%` },
}))<{ $left: number; $width: number; $color: string; $critical: boolean; $drag: boolean }>`
  position: absolute;
  top: 7px;
  height: 12px;
  min-width: 3px;
  border-radius: 4px;
  background: ${({ theme, $color, $critical }) =>
    fill($color, $critical, theme.colors.backgroundElevated)};
  box-shadow: ${({ theme, $critical }) =>
    $critical ? `0 0 0 1.5px ${theme.colors.status.danger}` : 'none'};
  cursor: ${({ $drag }) => ($drag ? 'grab' : 'pointer')};
  touch-action: none;
  user-select: none;
  &:active {
    cursor: ${({ $drag }) => ($drag ? 'grabbing' : 'pointer')};
  }
`;
const Baseline = styled.div.attrs<{ $left: number; $width: number }>(({ $left, $width }) => ({
  style: { left: `${$left}%`, width: `${$width}%` },
}))<{ $left: number; $width: number; $color: string }>`
  position: absolute;
  top: 21px;
  height: 4px;
  min-width: 2px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  pointer-events: none;
`;
const Summary = styled.div.attrs<{ $left: number; $width: number }>(({ $left, $width }) => ({
  style: { left: `${$left}%`, width: `${$width}%` },
}))<{ $left: number; $width: number }>`
  position: absolute;
  top: 10px;
  height: 7px;
  min-width: 3px;
  background: ${({ theme }) => theme.colors.text.secondary};
  opacity: 0.7;
  clip-path: polygon(0 0, 100% 0, 100% 100%, calc(100% - 4px) 55%, 4px 55%, 0 100%);
`;
const Diamond = styled.div.attrs<{ $left: number }>(({ $left }) => ({
  style: { left: `${$left}%` },
}))<{ $left: number; $critical: boolean }>`
  position: absolute;
  top: 8px;
  width: 11px;
  height: 11px;
  margin-left: -5px;
  transform: rotate(45deg);
  background: ${({ theme }) => theme.colors.primary};
  box-shadow: ${({ theme, $critical }) =>
    $critical ? `0 0 0 1.5px ${theme.colors.status.danger}` : 'none'};
`;
const Today = styled.div.attrs<{ $left: number }>(({ $left }) => ({
  style: { left: `${$left}%` },
}))<{ $left: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1;
  border-left: 2px dashed ${({ theme }) => theme.colors.text.secondary};
  opacity: 0.75;
  pointer-events: none;
`;
const Links = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  pointer-events: none;
  overflow: visible;
`;
const Arrow = styled.div.attrs<{ $left: number; $top: number }>(({ $left, $top }) => ({
  style: { left: `${$left}%`, top: `${$top}px` },
}))<{ $left: number; $top: number; $back: boolean }>`
  position: absolute;
  z-index: 2;
  width: 0;
  height: 0;
  margin-top: -4px;
  margin-left: ${({ $back }) => ($back ? '0' : '-6px')};
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
  ${({ theme, $back }) =>
    $back
      ? css`
          border-right: 6px solid ${theme.colors.text.secondary};
        `
      : css`
          border-left: 6px solid ${theme.colors.text.secondary};
        `}
  pointer-events: none;
`;
const DragHint = styled.span.attrs<{ $left: number }>(({ $left }) => ({
  style: { left: `${$left}%` },
}))<{ $left: number }>`
  position: absolute;
  top: 6px;
  margin-left: 8px;
  padding: 0 6px;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.backgroundElevated};
  background: ${({ theme }) => theme.colors.text.primary};
  pointer-events: none;
`;

const Empty = styled.p`
  margin: 0;
  padding: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/* ── Карточка работы ── */

const Side = styled.aside<{ $below: boolean }>`
  border-left: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: 14px;
  overflow-y: auto;
  height: min(72vh, 760px);
  min-height: 420px;
  font-size: 13px;
  h3 {
    margin: 4px 0 8px;
    font-size: 15px;
    line-height: 1.35;
  }
  ${({ $below, theme }) =>
    $below &&
    css`
      height: auto;
      min-height: 0;
      border-left: 0;
      border-top: 1px solid ${theme.colors.border};
      @media (min-width: 720px) {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr);
        grid-template-rows: auto auto auto 1fr;
        column-gap: 28px;
        align-items: start;
        & > * {
          grid-column: 1;
        }
        & > dl {
          grid-column: 2;
          grid-row: 1 / span 4;
          margin: 0;
        }
      }
    `}
`;
const SideHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;
const Close = styled.button`
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 20px;
  cursor: pointer;
  line-height: 1;
  padding: 2px 6px;
`;
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const Facts = styled.dl`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 7px 10px;
  margin: 12px 0 14px;
  dt {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
`;
const LinkButton = styled.button`
  border: 0;
  padding: 0;
  margin: 0 8px 2px 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  cursor: pointer;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
`;
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

/* ── Легенда ── */

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  align-items: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;
const Swatch = styled.i<{ $color: string; $height: number; $critical?: boolean }>`
  display: inline-block;
  width: 22px;
  height: ${({ $height }) => $height}px;
  margin-right: 6px;
  vertical-align: middle;
  border-radius: 3px;
  background: ${({ theme, $color, $critical }) =>
    fill($color, !!$critical, theme.colors.backgroundElevated)};
  box-shadow: ${({ theme, $critical }) =>
    $critical ? `0 0 0 1.5px ${theme.colors.status.danger}` : 'none'};
`;

const iso = (t: number) => new Date(t).toISOString();
const pad = (n: number) => String(n).padStart(2, '0');
/** Дата в ячейке таблицы по поясу объекта: «12.10.26». */
const cellDate = (t: number, offset: number) => {
  const d = new Date(t + offset);
  return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${pad(d.getUTCFullYear() % 100)}`;
};
/** Подпись деления: месяцы по-русски; крупные — «ноябрь 2026» или год. */
const tickLabel = (t: number, major: boolean, zoom: Zoom, offset: number) => {
  const d = new Date(t + offset);
  const month = d.getUTCMonth();
  if (major)
    return zoom === 'day' || zoom === 'week'
      ? `${MONTHS_FULL[month]} ${d.getUTCFullYear()}`
      : String(d.getUTCFullYear());
  if (zoom === 'day') return String(d.getUTCDate());
  if (zoom === 'week') return `${d.getUTCDate()} ${MONTHS[month]}`;
  return MONTHS[month];
};
const onKeys = (action: () => void) => (event: KeyboardEvent) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
};

export interface GanttProps {
  activities: Activity[];
  timings: ActivityTiming[];
  dependencies: Dependency[];
  wbs?: KspWorkspace['wbs'];
  rdBindings?: KspWorkspace['rd_bindings'];
  /** Привязки к РД пришли не все (сервер отдаёт страницами). */
  rdIncomplete?: boolean;
  onLoadRd?: () => void;
  /** Работ версии больше, чем загружено. */
  incomplete?: { loaded: number; total: number; onLoad: () => void } | null;
  timezone: string;
  /** Почему перенос перетаскиванием недоступен; null — доступен. */
  readonlyReason: string | null;
  onEdit: (activity: Activity) => void;
  onMove: (activity: Activity, proposed: string, affected: string[]) => void;
}

/**
 * График проекта как в программах календарного планирования: дерево разделов WBS с работами
 * слева, диаграмма Ганта справа (масштаб день / неделя / месяц / весь проект, «сегодня»,
 * базовый план, критические работы, вехи), связи и карточка выбранной работы. Рисуются только
 * видимые строки — 1000+ работ листаются плавно. Перенос — перетаскиванием полосы.
 */
export function Gantt({
  activities,
  timings,
  dependencies,
  wbs,
  rdBindings,
  rdIncomplete = false,
  onLoadRd,
  incomplete,
  timezone,
  readonlyReason,
  onEdit,
  onMove,
}: GanttProps) {
  const theme = useTheme();
  const planColor = theme.name === 'dark' ? '#3987e5' : '#2a78d6';
  const readonly = readonlyReason != null;
  const tree = useMemo(() => buildTree(activities, wbs ?? [], timings), [activities, wbs, timings]);
  const offset = useMemo(() => zoneOffset(timezone, Date.now()), [timezone]);
  const [open, setOpen] = useState<Set<string> | null>(null);
  const shownOpen = useMemo(() => open ?? defaultOpen(tree), [open, tree]);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<{ zoom: Zoom; range: Span }>(() => {
    const now = Date.now();
    const year = windowFor('month', now, tree.span, offset);
    const zoom: Zoom =
      !tree.span || (tree.span.finish >= year.start && tree.span.start <= year.finish)
        ? 'month'
        : 'all';
    return { zoom, range: windowFor(zoom, now, tree.span, offset) };
  });
  const { zoom, range } = view;
  const [selected, setSelected] = useState<string | null>(null);
  const [scroll, setScroll] = useState(0);
  const [viewport, setViewport] = useState(760);
  const [drag, setDrag] = useState<{ id: string; x: number; dx: number; width: number } | null>(
    null,
  );
  const scroller = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const [boardWidth, setBoardWidth] = useState(1240);
  useEffect(() => {
    const element = boardRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => setBoardWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const filtered = filter !== 'all' || search.trim() !== '';
  const rows = useMemo(
    () => visibleRows(tree, shownOpen, filter, search),
    [tree, shownOpen, filter, search],
  );
  const rowIndex = useMemo(() => new Map(rows.map((row, index) => [row.id, index])), [rows]);
  const first = Math.max(0, Math.floor(scroll / ROW) - 8);
  const slice = rows.slice(first, Math.min(rows.length, first + Math.ceil(viewport / ROW) + 16));
  const scaleTicks = useMemo(() => ticks(range, zoom, offset), [range, zoom, offset]);
  const now = Date.now();
  const todayAt = place({ start: now, finish: now }, range);
  const totals = useMemo(() => {
    let milestones = 0;
    for (const work of tree.works.values()) if (work.milestone) milestones += 1;
    const sum = (key: 'critical' | 'negative') =>
      tree.roots.reduce((acc, root) => acc + root[key], 0);
    return { critical: sum('critical'), negative: sum('negative'), milestones };
  }, [tree]);

  const selectedWork = selected ? tree.works.get(selected) : undefined;
  const incoming = useMemo(
    () => (selected ? dependencies.filter((link) => link.successor_id === selected) : []),
    [dependencies, selected],
  );
  const outgoing = useMemo(
    () => (selected ? dependencies.filter((link) => link.predecessor_id === selected) : []),
    [dependencies, selected],
  );
  const markOf = (id: string): Mark =>
    id === selected
      ? 'selected'
      : incoming.some((link) => link.predecessor_id === id)
        ? 'before'
        : outgoing.some((link) => link.successor_id === id)
          ? 'after'
          : undefined;
  const paths = useMemo(
    () => (selected ? linkPaths(selected, dependencies, rowIndex, tree.works, range) : []),
    [selected, dependencies, rowIndex, tree, range],
  );
  const bindings = useMemo(
    () => (selected ? (rdBindings ?? []).filter((b) => b.activity_id === selected) : []),
    [rdBindings, selected],
  );

  /** Смена масштаба: держим выбранную работу, иначе «сегодня» (если на экране), иначе центр. */
  const changeZoom = (next: Zoom) => {
    const anchor = selectedWork?.forecast?.start ?? (todayAt || zoom === 'all' ? now : null);
    setView({
      zoom: next,
      range:
        anchor != null
          ? windowFor(next, anchor, tree.span, offset)
          : centered(next, (range.start + range.finish) / 2, tree.span),
    });
  };
  const toggle = (id: string) => {
    if (filtered) return;
    const next = new Set(shownOpen);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setOpen(next);
  };
  /** Перейти к работе: раскрыть путь, сбросить отбор, прокрутить и показать на шкале. */
  const show = (id: string) => {
    const work = tree.works.get(id);
    const owner = tree.owner.get(id);
    const next = new Set(shownOpen);
    if (owner) for (const node of pathOf(tree, owner)) next.add(node.id);
    setOpen(next);
    setFilter('all');
    setSearch('');
    setSelected(id);
    const nextZoom = zoom === 'all' ? 'month' : zoom;
    if (work?.forecast)
      setView({
        zoom: nextZoom,
        range: windowFor(nextZoom, work.forecast.start, tree.span, offset),
      });
    const index = visibleRows(tree, next, 'all', '').findIndex((row) => row.id === id);
    if (index >= 0)
      requestAnimationFrame(() =>
        scroller.current?.scrollTo({ top: Math.max(0, index * ROW - viewport / 3) }),
      );
  };
  const finishDrag = (work: Work<Activity>, clientX: number) => {
    const current = drag;
    setDrag(null);
    if (!current || current.id !== work.activity.id || !work.forecast) return;
    const shift = dragShift(clientX - current.x, current.width, range, zoom);
    if (shift === 0) return;
    onMove(
      work.activity,
      iso(work.forecast.start + shift),
      successorsOf(dependencies, work.activity.id),
    );
  };
  const codeOf = (id: string) => tree.works.get(id)?.activity.code ?? '—';
  const nameOf = (id: string) => tree.works.get(id)?.activity.name ?? '';
  // Карточка сбоку — если после неё шкале остаётся место, иначе — под диаграммой.
  const side = !!selectedWork && boardWidth >= 1080;
  const available = boardWidth - (side ? 320 : 0);
  const { left, layout } = layoutFor(available);
  const scaleWidth = Math.max(100, available - left);
  /** Подписи шкалы без наложений: следующая — не ближе минимального зазора в пикселях. */
  const labels = useMemo(() => {
    const gapMinor = zoom === 'day' ? 24 : zoom === 'week' ? 50 : 34;
    const gapMajor = zoom === 'day' || zoom === 'week' ? 110 : 44;
    const px = (t: number) => (at(t, range) / 100) * scaleWidth;
    const minor: typeof scaleTicks = [];
    const major: typeof scaleTicks = [];
    let lastMinor = -Infinity;
    let lastMajor = -Infinity;
    for (const tick of scaleTicks) {
      const x = px(tick.t);
      if (tick.minor && x - lastMinor >= gapMinor) {
        minor.push(tick);
        lastMinor = x;
      }
      if (tick.major && x - lastMajor >= gapMajor) {
        major.push(tick);
        lastMajor = x;
      }
    }
    return { minor, major, lead: !major.length || px(major[0].t) >= gapMajor };
  }, [scaleTicks, range, scaleWidth, zoom]);

  const renderLeft = (row: Row<Activity>, index: number) => {
    const top = (first + index) * ROW;
    if (row.kind === 'wbs') {
      const node = row.node;
      return (
        <LeftLine
          key={row.id}
          $top={top}
          $group
          $layout={layout}
          role="button"
          tabIndex={0}
          aria-expanded={row.open}
          onClick={() => toggle(row.id)}
          onKeyDown={onKeys(() => toggle(row.id))}
          title={`${node.code} ${node.name}`.trim()}
        >
          <Name $indent={row.depth * 14} $group>
            <Toggle>{row.open ? '▾' : '▸'}</Toggle>
            {node.code && <Code>{node.code}</Code>}
            <b>{node.name}</b>
            <Count>{row.shown.toLocaleString('ru-RU')}</Count>
          </Name>
          <span />
          <span>
            {row.span ? Math.max(1, Math.round((row.span.finish - row.span.start) / DAY)) : ''}
          </span>
          <span>{row.span ? cellDate(row.span.start, offset) : ''}</span>
          <span>{row.span ? cellDate(row.span.finish, offset) : ''}</span>
          <span
            title={
              row.worst == null
                ? 'Резерв не рассчитан'
                : `Наименьший резерв в разделе. ${row.negative ? `Работ с отрицательным резервом: ${row.negative}.` : 'Работ за сроком нет.'}`
            }
          >
            <Late $late={(row.worst ?? 0) < 0}>{floatDays(row.worst)}</Late>
          </span>
        </LeftLine>
      );
    }
    const work = row.work;
    const a = work.activity;
    const late = (work.float ?? 0) < 0;
    return (
      <LeftLine
        key={row.id}
        $top={top}
        $mark={markOf(row.id)}
        $layout={layout}
        role="button"
        tabIndex={0}
        aria-pressed={selected === row.id}
        onClick={() => setSelected(row.id)}
        onKeyDown={onKeys(() => setSelected(row.id))}
        title={`${a.code} ${a.name}`}
      >
        <Name $indent={row.depth * 14}>
          <Toggle aria-hidden>{work.milestone ? '◆' : ''}</Toggle>
          <Code>{a.code}</Code>
          <b>{a.name}</b>
          {work.critical && (
            <Tag title="Критическая работа: задержка сдвигает окончание">крит.</Tag>
          )}
        </Name>
        <span>{a.quantity ? `${decimal(a.quantity)} ${a.unit ?? ''}` : ''}</span>
        <span>{spanDays(work) ?? '—'}</span>
        <span>{work.forecast ? cellDate(work.forecast.start, offset) : '—'}</span>
        <span>{work.forecast ? cellDate(work.forecast.finish, offset) : '—'}</span>
        <span title={work.float == null ? 'Резерв не рассчитан' : FLOAT_HELP}>
          <Late $late={late}>{floatDays(work.float)}</Late>
        </span>
      </LeftLine>
    );
  };

  const renderRight = (row: Row<Activity>, index: number) => {
    const top = (first + index) * ROW;
    if (row.kind === 'wbs') {
      const span = row.span ? place(row.span, range) : null;
      return (
        <Line key={row.id} $top={top} onClick={() => toggle(row.id)}>
          {span && <Summary $left={span.left} $width={span.width} />}
        </Line>
      );
    }
    const work = row.work;
    const bar = work.forecast ? place(work.forecast, range) : null;
    const base = work.baseline ? place(work.baseline, range) : null;
    const dragging = drag?.id === row.id ? drag : null;
    const shift = dragging ? (dragging.dx / Math.max(dragging.width, 1)) * 100 : 0;
    const snapped =
      dragging && work.forecast
        ? work.forecast.start + dragShift(dragging.dx, dragging.width, range, zoom)
        : null;
    return (
      <Line key={row.id} $top={top} $mark={markOf(row.id)} onClick={() => setSelected(row.id)}>
        {base && <Baseline $left={base.left} $width={base.width} $color={planColor} />}
        {bar &&
          (work.milestone ? (
            <Diamond
              $left={bar.left}
              $critical={work.critical}
              title={`${work.activity.code} · веха · ${shortDate(iso(work.forecast!.start), timezone)}`}
            />
          ) : (
            <Bar
              $left={bar.left + shift}
              $width={bar.width}
              $color={theme.colors.primary}
              $critical={work.critical}
              $drag={!readonly}
              title={`${work.activity.code} ${work.activity.name}`}
              onPointerDown={(event) => {
                if (readonly || event.button !== 0) return;
                event.stopPropagation();
                event.currentTarget.setPointerCapture(event.pointerId);
                const width = event.currentTarget.parentElement?.clientWidth ?? 1;
                setDrag({ id: row.id, x: event.clientX, dx: 0, width });
              }}
              onPointerMove={(event) => {
                if (drag?.id === row.id) setDrag({ ...drag, dx: event.clientX - drag.x });
              }}
              onPointerUp={(event) => finishDrag(work, event.clientX)}
              onPointerCancel={() => setDrag(null)}
            />
          ))}
        {bar && snapped != null && (
          <DragHint $left={bar.left + shift + bar.width}>
            начало {shortDate(iso(snapped), timezone)}
          </DragHint>
        )}
      </Line>
    );
  };

  return (
    <>
      {incomplete && (
        <Notice role="status">
          Загружено {incomplete.loaded.toLocaleString('ru-RU')} из{' '}
          {incomplete.total.toLocaleString('ru-RU')} работ — график неполный.
          <Button $size="sm" $variant="secondary" onClick={incomplete.onLoad}>
            Загрузить все работы
          </Button>
        </Notice>
      )}
      <Toolbar>
        <Segmented role="group" aria-label="Масштаб">
          {ZOOMS.map(([id, title]) => (
            <Segment
              key={id}
              type="button"
              aria-pressed={zoom === id}
              $active={zoom === id}
              onClick={() => changeZoom(id)}
            >
              {title}
            </Segment>
          ))}
        </Segmented>
        <Segmented role="group" aria-label="Сдвиг шкалы">
          <Segment
            type="button"
            title="Раньше"
            aria-label="Раньше"
            onClick={() => setView({ zoom, range: pan(range, -1) })}
          >
            ‹
          </Segment>
          <Segment
            type="button"
            onClick={() => setView({ zoom, range: windowFor(zoom, now, tree.span, offset) })}
          >
            Сегодня
          </Segment>
          <Segment
            type="button"
            title="Позже"
            aria-label="Позже"
            onClick={() => setView({ zoom, range: pan(range, 1) })}
          >
            ›
          </Segment>
        </Segmented>
        <Segmented role="group" aria-label="Отбор работ">
          {(
            [
              ['all', 'Все', null],
              ['critical', 'Критические', totals.critical],
              ['negative', 'Отрицательный резерв', totals.negative],
              ['milestones', 'Вехи', totals.milestones],
            ] as [Filter, string, number | null][]
          ).map(([id, title, n]) => (
            <Segment
              key={id}
              type="button"
              aria-pressed={filter === id}
              $active={filter === id}
              onClick={() => {
                setFilter(id);
                scroller.current?.scrollTo({ top: 0 });
              }}
            >
              {title}
              {n != null && ` · ${n.toLocaleString('ru-RU')}`}
            </Segment>
          ))}
        </Segmented>
        <Search
          type="search"
          aria-label="Поиск работы или раздела"
          placeholder="Код или название работы, раздел…"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            scroller.current?.scrollTo({ top: 0 });
          }}
        />
      </Toolbar>
      <Small>
        {tree.works.size.toLocaleString('ru-RU')} работ в {tree.nodes.size.toLocaleString('ru-RU')}{' '}
        разделах
        {tree.span &&
          ` · ${shortDate(iso(tree.span.start), timezone)} — ${shortDate(iso(tree.span.finish), timezone)}`}
        {filtered &&
          ` · найдено ${rows.filter((row) => row.kind === 'work').length.toLocaleString('ru-RU')}`}
        {readonly
          ? ` · перенос перетаскиванием недоступен: ${readonlyReason}`
          : ' · полосу работы можно перетащить — откроется перенос с пересчётом'}
      </Small>
      <Board ref={boardRef} $side={side}>
        <Scroller
          ref={scroller}
          $short={!!selectedWork && !side}
          onScroll={(event) => {
            setScroll(event.currentTarget.scrollTop);
            setViewport(event.currentTarget.clientHeight);
          }}
        >
          <Head $left={left}>
            <HeadCells $layout={layout}>
              <span>
                Работа
                <HeadAction
                  type="button"
                  title="Развернуть все разделы"
                  disabled={filtered}
                  onClick={() => setOpen(new Set(tree.nodes.keys()))}
                >
                  развернуть
                </HeadAction>
                <HeadAction
                  type="button"
                  title="Свернуть до объектов"
                  disabled={filtered}
                  onClick={() => setOpen(null)}
                >
                  свернуть
                </HeadAction>
              </span>
              <span>Объём</span>
              <span title="Календарных дней">Дн.</span>
              <span>Начало</span>
              <span>Окончание</span>
              <span title={FLOAT_HELP}>Резерв</span>
            </HeadCells>
            <Scale aria-hidden>
              {labels.lead && (
                <TickLabel $left={0} $major>
                  {tickLabel(range.start, true, zoom, offset)}
                </TickLabel>
              )}
              {labels.major.map((tick) => (
                <TickLabel key={`M${tick.t}`} $left={at(tick.t, range)} $major>
                  {tickLabel(tick.t, true, zoom, offset)}
                </TickLabel>
              ))}
              {labels.minor.map((tick) => (
                <TickLabel key={`m${tick.t}`} $left={at(tick.t, range)}>
                  {tickLabel(tick.t, false, zoom, offset)}
                </TickLabel>
              ))}
            </Scale>
          </Head>
          <Body $height={Math.max(rows.length * ROW, ROW)} $left={left}>
            <LeftCol>{slice.map(renderLeft)}</LeftCol>
            <RightCol>
              {scaleTicks.map((tick) => (
                <GridLine key={tick.t} $left={at(tick.t, range)} $major={tick.major} />
              ))}
              {todayAt && <Today $left={todayAt.left} title="Сегодня" />}
              {slice.map(renderRight)}
              {paths.length > 0 && (
                <Links
                  viewBox={`0 0 100 ${Math.max(rows.length * ROW, ROW)}`}
                  preserveAspectRatio="none"
                  aria-hidden
                >
                  {paths.map((p) => {
                    const y1 = p.y1 * ROW + MID;
                    const y2 = p.y2 * ROW + MID;
                    const xm = p.x1 + 0.6;
                    return (
                      <path
                        key={p.key}
                        d={`M ${p.x1} ${y1} H ${xm} V ${y2} H ${p.x2}`}
                        fill="none"
                        stroke={theme.colors.text.secondary}
                        strokeWidth={1.5}
                        vectorEffect="non-scaling-stroke"
                      />
                    );
                  })}
                </Links>
              )}
              {paths.map((p) => (
                <Arrow
                  key={`a-${p.key}`}
                  $left={p.x2}
                  $top={p.y2 * ROW + MID}
                  $back={p.x2 < p.x1 + 0.6}
                />
              ))}
            </RightCol>
          </Body>
          {rows.length === 0 && <Empty>Ничего не найдено — измените отбор или поиск.</Empty>}
        </Scroller>
        {selectedWork && (
          <Side aria-label="Карточка работы" $below={!side}>
            <SideHead>
              <span>
                {selectedWork.activity.code} · {selectedWork.milestone ? 'веха' : 'работа'}
              </span>
              <Close type="button" aria-label="Закрыть карточку" onClick={() => setSelected(null)}>
                ×
              </Close>
            </SideHead>
            <h3>{selectedWork.activity.name}</h3>
            <Chips>
              {selectedWork.critical && <Chip $tone="bad">критическая</Chip>}
              {(selectedWork.float ?? 0) < 0 && (
                <Chip $tone="bad">за сроком на {floatDays(-(selectedWork.float ?? 0))}</Chip>
              )}
            </Chips>
            <Facts>
              <dt>Сроки</dt>
              <dd>
                {selectedWork.forecast
                  ? selectedWork.milestone
                    ? shortDate(iso(selectedWork.forecast.start), timezone)
                    : `${shortDate(iso(selectedWork.forecast.start), timezone)} — ${shortDate(iso(selectedWork.forecast.finish), timezone)}`
                  : 'прогноз не рассчитан'}
              </dd>
              {!selectedWork.milestone && (
                <>
                  <dt>Длительность</dt>
                  <dd>{spanDays(selectedWork) ?? '—'} дн.</dd>
                </>
              )}
              {selectedWork.activity.quantity && (
                <>
                  <dt>Объём</dt>
                  <dd>
                    {decimal(selectedWork.activity.quantity)} {selectedWork.activity.unit ?? ''}
                  </dd>
                </>
              )}
              <dt title={FLOAT_HELP}>Резерв</dt>
              <dd>{floatDays(selectedWork.float)}</dd>
              <dt>Базовый план</dt>
              <dd>
                {selectedWork.baseline
                  ? `${shortDate(iso(selectedWork.baseline.start), timezone)} — ${shortDate(iso(selectedWork.baseline.finish), timezone)}`
                  : 'не утверждён'}
              </dd>
              <dt>Раздел</dt>
              <dd>
                {pathOf(tree, tree.owner.get(selectedWork.activity.id) ?? '')
                  .map((node) => node.name)
                  .slice(-2)
                  .join(' / ') || '—'}
              </dd>
              <dt>РД</dt>
              <dd>
                {bindings.length > 0 &&
                  bindings
                    .slice(0, 3)
                    .map((b) => `${b.record_uid}: ${decimal(b.allocated_quantity)} ${b.unit}`)
                    .join('; ') + (bindings.length > 3 ? ` и ещё ${bindings.length - 3}` : '')}
                {bindings.length === 0 && !rdIncomplete && 'не привязана'}
                {rdIncomplete && (
                  <>
                    {bindings.length === 0
                      ? 'среди загруженных привязок нет'
                      : ' · загружены не все'}{' '}
                    {onLoadRd && (
                      <LinkButton type="button" onClick={onLoadRd}>
                        загрузить все
                      </LinkButton>
                    )}
                  </>
                )}
              </dd>
              <dt>До неё</dt>
              <dd>
                {incoming.length
                  ? incoming.slice(0, 12).map((link) => (
                      <LinkButton
                        key={link.id}
                        type="button"
                        title={`${nameOf(link.predecessor_id)} · ${linkLabel(link)}`}
                        onClick={() => show(link.predecessor_id)}
                      >
                        {codeOf(link.predecessor_id)}
                      </LinkButton>
                    ))
                  : '—'}
                {incoming.length > 12 && ` и ещё ${incoming.length - 12}`}
              </dd>
              <dt>После</dt>
              <dd>
                {outgoing.length
                  ? outgoing.slice(0, 12).map((link) => (
                      <LinkButton
                        key={link.id}
                        type="button"
                        title={`${nameOf(link.successor_id)} · ${linkLabel(link)}`}
                        onClick={() => show(link.successor_id)}
                      >
                        {codeOf(link.successor_id)}
                      </LinkButton>
                    ))
                  : '—'}
                {outgoing.length > 12 && ` и ещё ${outgoing.length - 12}`}
              </dd>
            </Facts>
            <Actions>
              <Button $size="sm" $variant="secondary" onClick={() => onEdit(selectedWork.activity)}>
                {readonly ? 'Подробнее' : 'Изменить работу'}
              </Button>
              {selectedWork.forecast && (
                <Button
                  $size="sm"
                  $variant="ghost"
                  onClick={() => {
                    const nextZoom = zoom === 'all' ? 'month' : zoom;
                    setView({
                      zoom: nextZoom,
                      range: windowFor(nextZoom, selectedWork.forecast!.start, tree.span, offset),
                    });
                  }}
                >
                  Показать на шкале
                </Button>
              )}
            </Actions>
          </Side>
        )}
      </Board>
      <Legend aria-label="Обозначения">
        <span>
          <Swatch $color={theme.colors.primary} $height={10} />
          Прогноз
        </span>
        <span>
          <Swatch $color={theme.colors.primary} $height={10} $critical />
          Критическая работа
        </span>
        <span>
          <Swatch $color={planColor} $height={4} />
          Базовый план
        </span>
        <span>
          <Swatch $color={theme.colors.text.secondary} $height={7} />
          Раздел
        </span>
        <span>◆ веха</span>
        <span>┆ сегодня</span>
        <span>линии — связи выбранной работы</span>
      </Legend>
    </>
  );
}
